<template lang="pug">
    div.chart(ref='chart' :style="{height: `${height || 600}px`}")
</template>

<script>
import * as echarts from 'echarts/core';
import {
    ToolboxComponent,
    TooltipComponent,
    GridComponent,
    VisualMapComponent,
    LegendComponent,
    BrushComponent,
    DataZoomComponent,
    MarkLineComponent,
    MarkPointComponent,
} from 'echarts/components';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
echarts.use([
    ToolboxComponent,
    TooltipComponent,
    GridComponent,
    VisualMapComponent,
    LegendComponent,
    BrushComponent,
    DataZoomComponent,
    CandlestickChart,
    LineChart,
    BarChart,
    CanvasRenderer,
    UniversalTransition,
    MarkLineComponent,
    MarkPointComponent,
]);

let resizeCallback = null;

export default {
    name: 'CandleStick',
    props: { dailydata: Array, height: Number, products: Array },
    data() {
        return {
            rendered: false,
            $chart: null,
        };
    },
    watch: {
        dailydata(v) {
            this.draw();
        },
        products: {
            handler() {
                this.draw();
            },
            immediate: true,
        },
    },
    methods: {
        draw() {
            const self = this;
            if (!this.dailydata || !this.dailydata.length) return;
            if (!this.$data.$chart) {
                this.$data.$chart = echarts.init(this.$refs.chart, 'dark');
                const self = this;
                resizeCallback = () => {
                    self.resizeHandler();
                };

                window.addEventListener('resize', resizeCallback);

                this.$data.$chart.on('click', 'series', function (e) {
                    self.$emit('markPointClick', e);
                });
            } else {
                this.$data.$chart.clear();
            }
            let option;
            const dailydata = [...this.dailydata].reverse();
            const upColor = '#ec0000';
            const downColor = '#00da3c';
            function splitData(rawData) {
                let categoryData = [];
                let values = [];
                let volumes = [];
                rawData.forEach((item, i) => {
                    const date = new Date(item.trade_date * 1000);
                    categoryData.push(
                        `${date.getFullYear()}-${(date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0')}-${date
                            .getDate()
                            .toString()
                            .padStart(2, '0')}`
                    );
                    values.push([
                        item.open_price,
                        item.close_price,
                        item.low_price,
                        item.high_price,
                    ]);
                    volumes.push([
                        i,
                        item.volume,
                        values[i][0] > values[i][1] ? 1 : -1,
                    ]);
                });
                return {
                    categoryData: categoryData,
                    values: values,
                    volumes: volumes,
                };
            }
            function calculateMA(dayCount, data) {
                var result = [];
                for (var i = 0, len = data.values.length; i < len; i++) {
                    if (i < dayCount) {
                        result.push('-');
                        continue;
                    }
                    var sum = 0;
                    for (var j = 0; j < dayCount; j++) {
                        sum += data.values[i - j][1];
                    }
                    result.push(+(sum / dayCount).toFixed(2));
                }
                return result;
            }

            var data = splitData(dailydata);

            const markPoint = {
                data:
                    this.products &&
                    this.products
                        .map(({ items }) => items)
                        .flat()
                        .map(item => {
                            const dataObj = dailydata.find(dataItem => {
                                const date = new Date(
                                    dataItem.trade_date * 1000
                                );
                                return (
                                    `${date.getFullYear()}-${(
                                        date.getMonth() + 1
                                    )
                                        .toString()
                                        .padStart(2, '0')}-${date
                                        .getDate()
                                        .toString()
                                        .padStart(2, '0')}` == item.date_str
                                );
                            });
                            return {
                                value: '影像',
                                xAxis: item.date_str,
                                yAxis:
                                    (dataObj && dataObj.close_price) || 'max',
                            };
                        }),
            };
            option = {
                backgroundColor: 'rgba(0,0,0,0)',
                animation: false,
                legend: {
                    bottom: 10,
                    left: 'center',
                    data: [
                        '日K',
                        '5日均线',
                        '10日均线',
                        '20日均线',
                        '30日均线',
                    ],
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'cross',
                    },
                    borderWidth: 1,
                    // borderColor: '#ccc',
                    padding: 10,
                    position: function (pos, params, el, elRect, size) {
                        const obj = {
                            top: 10,
                        };
                        obj[
                            ['left', 'right'][+(pos[0] < size.viewSize[0] / 2)]
                        ] = 30;
                        return obj;
                    },
                    formatter: function (params) {
                        const tooltipList = [];
                        params.forEach(param => {
                            if (param.seriesType != 'candlestick') {
                                const value = Array.isArray(param.value)
                                    ? param.value[1]
                                    : isNaN(param.value)
                                    ? param.value
                                    : param.value.toFixed(2);
                                return tooltipList.push(
                                    `${param.seriesName} : ${value}`
                                );
                            }
                            tooltipList.push(
                                [
                                    '日期: ' +
                                        param.name +
                                        '<hr size=1 style="margin: 3px 0">',
                                    '开盘价: ' +
                                        param.data[1].toFixed(2) +
                                        '<br/>',
                                    '收盘价: ' +
                                        param.data[2].toFixed(2) +
                                        '<br/>',
                                    '最低价: ' +
                                        param.data[3].toFixed(2) +
                                        '<br/>',
                                    '最高价: ' + param.data[4].toFixed(2),
                                ].join('')
                            );
                        });
                        return tooltipList.join('<br/>');
                    },

                    // extraCssText: 'width: 170px'
                },
                axisPointer: {
                    link: [
                        {
                            xAxisIndex: 'all',
                        },
                    ],
                    // label: {
                    //     backgroundColor: '#fff',
                    // },
                },
                toolbox: {
                    feature: {
                        dataZoom: {
                            yAxisIndex: false,
                        },
                        brush: {
                            type: ['lineX', 'clear'],
                        },
                    },
                },
                brush: {
                    xAxisIndex: 'all',
                    brushLink: 'all',
                    outOfBrush: {
                        colorAlpha: 0.1,
                    },
                },
                visualMap: {
                    show: false,
                    seriesIndex: 5,
                    dimension: 2,
                    pieces: [
                        {
                            value: 1,
                            color: downColor,
                        },
                        {
                            value: -1,
                            color: upColor,
                        },
                    ],
                },
                grid: [
                    {
                        left: '10%',
                        right: '8%',
                        height: '50%',
                    },
                    {
                        left: '10%',
                        right: '8%',
                        top: '63%',
                        height: '16%',
                    },
                ],
                xAxis: [
                    {
                        type: 'category',
                        data: data.categoryData,
                        scale: true,
                        boundaryGap: false,
                        axisLine: {
                            lineStyle: { color: '#8392A5' },
                            onZero: false,
                        },
                        splitLine: { show: false },
                        min: 'dataMin',
                        max: 'dataMax',
                        axisPointer: {
                            z: 100,
                        },
                        axisLabel: {
                            rotate: 40,
                        },
                    },
                    {
                        type: 'category',
                        gridIndex: 1,
                        data: data.categoryData,
                        scale: true,

                        boundaryGap: false,
                        axisLine: {
                            lineStyle: { color: '#8392A5' },
                            onZero: false,
                        },
                        axisTick: { show: false },
                        splitLine: { show: false },
                        axisLabel: {
                            show: false,
                        },
                        min: 'dataMin',
                        max: 'dataMax',
                    },
                ],
                yAxis: [
                    {
                        scale: true,
                        splitArea: {
                            show: true,
                        },
                        axisLine: { lineStyle: { color: '#8392A5' } },
                    },
                    {
                        scale: true,
                        gridIndex: 1,
                        splitNumber: 2,
                        axisLabel: {
                            show: false,
                        },
                        axisLine: { show: false },
                        axisTick: { show: false },
                        splitLine: { show: false },
                    },
                ],
                dataZoom: [
                    {
                        type: 'inside',
                        xAxisIndex: [0, 1],
                        minSpan: 10,
                        start: data.categoryData.length - 50,
                        end: data.categoryData.length,
                    },
                    {
                        show: true,
                        xAxisIndex: [0, 1],
                        type: 'slider',
                        top: '85%',
                        minSpan: 10,
                        start: data.categoryData.length - 50,
                        end: data.categoryData.length,
                    },
                ],
                series: [
                    {
                        name: '日K',
                        type: 'candlestick',
                        data: data.values,
                        itemStyle: {
                            color: upColor,
                            color0: downColor,
                            borderColor: undefined,
                            borderColor0: undefined,
                        },
                        markPoint,
                    },
                    {
                        name: '5日均线',
                        type: 'line',
                        data: calculateMA(5, data),
                        smooth: true,
                        lineStyle: {
                            opacity: 0.5,
                        },
                    },
                    {
                        name: '10日均线',
                        type: 'line',
                        data: calculateMA(10, data),
                        smooth: true,
                        lineStyle: {
                            opacity: 0.5,
                        },
                    },
                    {
                        name: '20日均线',
                        type: 'line',
                        data: calculateMA(20, data),
                        smooth: true,
                        lineStyle: {
                            opacity: 0.5,
                        },
                    },
                    {
                        name: '30日均线',
                        type: 'line',
                        data: calculateMA(30, data),
                        smooth: true,
                        lineStyle: {
                            opacity: 0.5,
                        },
                    },
                    {
                        name: '成交量',
                        type: 'bar',
                        xAxisIndex: 1,
                        yAxisIndex: 1,
                        data: data.volumes,
                    },
                ],
            };

            option && this.$data.$chart.setOption(option);
        },
        resizeHandler() {
            this.$data.$chart && this.$data.$chart.resize();
        },
    },
    mounted() {},
    destroyed() {
        resizeCallback && window.removeEventListener('resize', resizeCallback);
        this.$data.$chart && this.$data.$chart.dispose();
    },
};
</script>

<style lang="stylus" scoped>
.chart
    width 100%
    min-height 200px
</style>
